import { useRequestMedicalContext } from "../Contexts/RequestMedicalTestContext";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CircularProgress, Divider, Grid } from "@mui/material";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { baseUrl } from "../utilities/configurations";
import apiRequest from "../utilities/request";
import { useEffect, useState } from "react";
import clockIcon from "../assets/clock.png";
import { ScrollBar } from "./ScrollBar";
import dayjs from "dayjs";
import {
  KeyboardArrowRightOutlined,
  KeyboardArrowLeftOutlined,
  CalendarTodayRounded,
  CheckRounded,
} from "@mui/icons-material";
import ErrorComponent from "./ErrorComponent";

const ProceduresBooking = () => {
  const {
    setSelectedProcedureClass,
    selectedProcedureClass,
    setSelectedProcedures,
    selectedProcedures,
    setSelectedClinic,
    selectedClinic,
    setIsLoading,
    isLoading,
    setMonth,
    setYear,
    files,
    month,
    year,
  } = useRequestMedicalContext();

  const [openDatePicker, setOpenDatePicker] = useState({});
  const [availableDateLoadState, setAvailableDateLoadState] = useState("");

  const handleOpenDatePicker = async (index) => {
    // Call handleOpenDatePicker after updating the procedures
    setOpenDatePicker((prevOpenDatePicker) => ({
      ...prevOpenDatePicker,
      [index]: true,
    }));
  };

  const handleCloseDatePicker = (index) => {
    setOpenDatePicker((prevOpenDatePicker) => ({
      ...prevOpenDatePicker,
      [index]: false, // Close the date picker for the corresponding index
    }));
  };

  const [edit, setEdit] = useState({});

  const handleEditClick = (index) => {
    // Toggle the edit state for the specific procedure
    setEdit((prevEdit) => ({
      ...prevEdit,
      [index]: !prevEdit[index],
    }));
  };

  const fetchAvailableDates = async (state) => {
    setAvailableDateLoadState(state || "get");
    try {
      const updatedProcedures = await Promise.all(
        selectedClinic.procedures.map(async (procedure) => {
          const response = await apiRequest(
            `${baseUrl}/medical-test/dates?month=${year}-${month}&providerId=${procedure.id}&calendarId=${procedure.calendarId}`,
            "GET"
          );
          return { ...procedure, availableDates: response.data };
        })
      );

      setSelectedClinic((prevClinic) => ({
        ...prevClinic,
        procedures: updatedProcedures,
      }));
      setAvailableDateLoadState("success");
    } catch (error) {
      setAvailableDateLoadState("error");
      console.error("Error fetching available dates:", error);
    }
  };

  useEffect(() => {
    if (selectedClinic && selectedClinic.procedures.length > 0) {
      fetchAvailableDates();
    }
  }, [year, month]);

  const getAvailableTimes = async (configuredDate, index) => {
    setIsLoading(true);
    try {
      const updatedProcedures = await Promise.all(
        selectedClinic.procedures.map(async (procedure) => {
          const res = await apiRequest(
            baseUrl +
              `/medical-test/times?date=${configuredDate}&providerId=${procedure.id}&calendarId=${procedure.calendarId}`,
            "GET"
          );
          return { ...procedure, availableTimes: res.data };
        })
      );

      setSelectedClinic((prevClinic) => ({
        ...prevClinic,
        procedures: updatedProcedures,
      }));
    } catch (error) {
      console.error("Error fetching available dates:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderDatePicker = (data, index) => {
    let procedureIndex;
    let foundProcedure;

    if (files.length === 0) {
      foundProcedure = selectedClinic.procedures.find((procedure) =>
        selectedProcedures[index].coveredProcedures.some((item) => {
          let itemClassPart = item.class.replace(" PROCEDURES", "");

          // Additional modifications for specific cases
          if (itemClassPart === "12 - L ECG") {
            itemClassPart = "ECG";
          } else if (
            itemClassPart === "2-D ECHO WITH DOPPLER STUDIES (PEDIA)"
          ) {
            itemClassPart = "2D ECHO (PEDIA)";
          } else if (itemClassPart === "2-D ECHO WITH DOPPLER STUDIES") {
            itemClassPart = "2D ECHO";
          }
          const isSubclass =
            item.subclass === (item = item.subclass || itemClassPart);
          return (
            procedure.procedureName
              .toLowerCase()
              .includes(item.toLowerCase()) ||
            (!isSubclass &&
              procedure.procedureName
                .toLowerCase()
                .split(":")[0]
                .includes(item.toLowerCase().split(" ")[0]))
          );
        })
      );
      procedureIndex = selectedClinic.procedures.indexOf(foundProcedure);
    } else {
      foundProcedure = selectedClinic.procedures.find((procedure) => {
        const procedureClassPart = procedure.procedureName.toUpperCase();
        const selectedItemName =
          selectedProcedureClass[index].name.toUpperCase();

        // Modify the selected item name as needed for comparison
        let itemClassPart = selectedItemName
          .slice(selectedItemName.indexOf(":") + 2)
          .replace(" PROCEDURES", "");

        // Additional modifications for specific cases
        if (itemClassPart === "12 - L ECG") {
          itemClassPart = "ECG";
        } else if (itemClassPart === "2-D ECHO WITH DOPPLER STUDIES (PEDIA)") {
          itemClassPart = "2D ECHO (PEDIA)";
        } else if (itemClassPart === "2-D ECHO WITH DOPPLER STUDIES") {
          itemClassPart = "2D ECHO";
        }

        // Perform comparison
        return procedureClassPart.startsWith(itemClassPart);
      });
      procedureIndex = selectedClinic.procedures.indexOf(foundProcedure);
    }

    return (
      openDatePicker[index] && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MobileDatePicker
            disablePast={true}
            open={openDatePicker[index]}
            onClose={() => handleCloseDatePicker(index)}
            value={data?.date ? dayjs(data?.date) : undefined}
            shouldDisableDate={(date) => {
              const dateString = dayjs(date).format("YYYY-MM-DD");
              return !selectedClinic.procedures[
                procedureIndex
              ].availableDates?.some(
                (availableDate) => availableDate.date === dateString
              );
            }}
            onAccept={async (value) => {
              if (value !== null) {
                const configuredDate = dayjs(value).format("YYYY-MM-DD");
                const procedureData = {
                  calendarId:
                    selectedClinic.procedures[procedureIndex].calendarId,
                  id: selectedClinic.procedures[procedureIndex].id,
                  date: configuredDate,
                  time: "",
                };

                if (files.length > 0) {
                  setSelectedProcedureClass((prevSelectedProcedures) => {
                    const updatedProcedures = [...prevSelectedProcedures];
                    updatedProcedures[index] = {
                      ...updatedProcedures[index],
                      ...procedureData,
                    };
                    return updatedProcedures;
                  });
                  getAvailableTimes(configuredDate);
                } else {
                  setSelectedProcedures((prevSelectedProcedures) => {
                    const updatedProcedures = [...prevSelectedProcedures];
                    updatedProcedures[index] = {
                      ...updatedProcedures[index],
                      ...procedureData,
                    };
                    return updatedProcedures;
                  });
                  getAvailableTimes(configuredDate, index);
                }
              }
            }}
            onMonthChange={async (value) => {
              setMonth(dayjs(value).format("MM"));
            }}
            onYearChange={async (value) => {
              setYear(dayjs(value).format("YYYY"));
            }}
            sx={{
              "& .MuiInputBase-root": {
                display: "none",
              },
            }}
            slotProps={{
              dialog: {
                onClose: (e, reason) => {
                  if (reason === "backdropClick") {
                    return;
                  }
                },
              },
            }}
          />
        </LocalizationProvider>
      )
    );
  };

  let isSameOrBefore = require("dayjs/plugin/isSameOrBefore");

  const nextDay = async (data, index) => {
    const configuredDate = dayjs(data?.date).add(1, "day");
    if (files.length > 0) {
      setSelectedProcedureClass((prevSelectedProcedures) => {
        const updatedProcedures = [...prevSelectedProcedures];
        updatedProcedures[index] = {
          ...updatedProcedures[index],
          date: configuredDate,
        };
        return updatedProcedures;
      });
    } else {
      setSelectedProcedures((prevSelectedProcedures) => {
        const updatedProcedures = [...prevSelectedProcedures];
        updatedProcedures[index] = {
          ...updatedProcedures[index],
          date: configuredDate,
        };
        return updatedProcedures;
      });
    }
    getAvailableTimes(dayjs(configuredDate).format("YYYY-MM-DD"));
  };

  const previousDay = async (data, index) => {
    dayjs.extend(isSameOrBefore);
    const a = dayjs().isSameOrBefore(data?.date);
    if (a === true) {
      const configuredDate = dayjs(data?.date).subtract(1, "day");
      if (files.length > 0) {
        setSelectedProcedureClass((prevSelectedProcedures) => {
          const updatedProcedures = [...prevSelectedProcedures];
          updatedProcedures[index] = {
            ...updatedProcedures[index],
            date: configuredDate,
          };
          return updatedProcedures;
        });
      } else {
        setSelectedProcedures((prevSelectedProcedures) => {
          const updatedProcedures = [...prevSelectedProcedures];
          updatedProcedures[index] = {
            ...updatedProcedures[index],
            date: configuredDate,
          };
          return updatedProcedures;
        });
      }
      getAvailableTimes(dayjs(configuredDate).format("YYYY-MM-DD"));
    }
  };

  let timeSlotLoading = (
    <div className="w-full flex justify-center items-center mt-4">
      <CircularProgress />
    </div>
  );

  let noTimeSlots = (
    <div className="w-full flex flex-col justify-center items-center mt-2">
      <img src={clockIcon} alt="no-timeslot" className="w-24 h-24" />
      <p>No timeslots available</p>
    </div>
  );
  //removed the async await
  const handleFilesTimeslotChange = (timeData, index) => {
    setSelectedProcedureClass((prevSelectedProcedures) => {
      const updatedProcedures = [...prevSelectedProcedures];
      updatedProcedures[index] = {
        ...updatedProcedures[index],
        time: timeData.time,
      };
      return updatedProcedures;
    });
  };

  const renderFilesTimeSlots = (data, index) => {
    if (isLoading) {
      return timeSlotLoading;
    } else if (data?.length > 0) {
      return data.map((timeData) => (
        <Grid className="mt-3" key={timeData} item xs={6} sm={6} md={6} lg={6}>
          <label id="labl">
            <input
              name="time"
              type="radio"
              checked={selectedProcedureClass[index]?.time === timeData.time}
              onChange={() => handleFilesTimeslotChange(timeData, index)}
            />
            <div className="border rounded-lg p-3 cursor-pointer">
              <div className="text-sm text-center">
                {`${dayjs(timeData.time).format("h:mm A")}`}
              </div>
            </div>
          </label>
        </Grid>
      ));
    } else {
      return noTimeSlots;
    }
  };

  const handleTimeSlotChange = (timeData, index) => {
    setSelectedProcedures((prevSelectedProcedures) => {
      const updatedProcedures = [...prevSelectedProcedures];
      updatedProcedures[index] = {
        ...updatedProcedures[index],
        time: timeData.time,
      };
      return updatedProcedures;
    });
  };

  const renderTimeSlots = (data, selectedProcedure, index) => {
    if (isLoading) {
      return timeSlotLoading;
    } else if (data?.length > 0) {
      return data.map((timeData) => (
        <Grid className="mt-3" key={timeData} item xs={6} sm={6} md={6} lg={6}>
          <label id="labl">
            <input
              name="time"
              type="radio"
              checked={selectedProcedure?.time === timeData.time}
              onChange={() => handleTimeSlotChange(timeData, index)}
            />
            <div className="border rounded-lg p-3 cursor-pointer">
              <div className="text-sm text-center">
                {`${dayjs(timeData.time).format("h:mm A")}`}
              </div>
            </div>
          </label>
        </Grid>
      ));
    } else {
      return noTimeSlots;
    }
  };

  const renderTimeSection = (title, data, index) => {

    const selectedProcedure = selectedProcedures[index];
    let procedures = selectedProcedures[index];
    let timeSlotFunc = renderTimeSlots(data, selectedProcedure, index);
    if(files.length > 0){
      procedures = selectedProcedureClass[index];
      timeSlotFunc = renderFilesTimeSlots(data, index);
    }

    return (
      procedures?.date && (
        <div>
          <Divider
            textAlign="left"
            sx={{
              "& .MuiDivider-wrapper": {
                padding: 0,
                paddingRight: 2,
              },
              "&::before": { width: 0 },
            }}
          >
            {title}
          </Divider>
          <Grid container spacing={2} style={{ marginTop: "4px" }}>
            {timeSlotFunc}
          </Grid>
        </div>
      )
    );
  };

  const morningTimes = (index) => {
    if (files.length > 0) {
      const foundProcedure = selectedClinic.procedures.find((procedure) => {
        const procedureClassPart = procedure.procedureName.toUpperCase();
        const selectedItemName =
          selectedProcedureClass[index].name.toUpperCase();

        // Modify the selected item name as needed for comparison
        let itemClassPart = selectedItemName
          .slice(selectedItemName.indexOf(":") + 2)
          .replace(" PROCEDURES", "");

        // Additional modifications for specific cases
        if (itemClassPart === "12 - L ECG") {
          itemClassPart = "ECG";
        } else if (itemClassPart === "2-D ECHO WITH DOPPLER STUDIES (PEDIA)") {
          itemClassPart = "2D ECHO (PEDIA)";
        } else if (itemClassPart === "2-D ECHO WITH DOPPLER STUDIES") {
          itemClassPart = "2D ECHO";
        }

        // Perform comparison
        return procedureClassPart.startsWith(itemClassPart);
      });
      const procedureIndex = selectedClinic.procedures.indexOf(foundProcedure);
      return selectedClinic.procedures[procedureIndex].availableTimes?.filter(
        (data) => dayjs(data?.time).hour() < 12
      );
    } else {
      const procedureToFind = selectedProcedures[index].coveredProcedures.map(
        (procedure) => {
          let itemClassPart = procedure.class
            .slice(procedure.class.indexOf(":") + 2)
            .replace(" PROCEDURES", "");

          // Additional modifications for specific cases
          if (itemClassPart === "12 - L ECG") {
            itemClassPart = "ECG";
          } else if (
            itemClassPart === "2-D ECHO WITH DOPPLER STUDIES (PEDIA)"
          ) {
            itemClassPart = "2D ECHO (PEDIA)";
          } else if (itemClassPart === "2-D ECHO WITH DOPPLER STUDIES") {
            itemClassPart = "2D ECHO";
          }
          return procedure.subclass ? procedure.subclass : itemClassPart;
        }
      );

      const foundProcedure = selectedClinic.procedures.find((procedure) =>
        procedureToFind.some((item) => {
          const isSubclass = selectedProcedures[index].coveredProcedures.some(
            (p) => p.subclass === item
          );
          if (isSubclass) {
            return procedure.procedureName
              .toLowerCase()
              .includes(item.toLowerCase());
          } else {
            const procedureClassPart = procedure.procedureName
              .split(":")[0]
              .trim()
              .toLowerCase();
            const itemClassPart = item.split(" ")[0].trim().toLowerCase();
            return procedureClassPart.includes(itemClassPart);
          }
        })
      );

      if (foundProcedure) {
        const procedureIndex =
          selectedClinic.procedures.indexOf(foundProcedure);
        return selectedClinic.procedures[procedureIndex].availableTimes?.filter(
          (data) => dayjs(data?.time).hour() < 12
        );
      }
    }
  };

  const afternoonTimes = (index) => {
    if (files.length > 0) {
      const foundProcedure = selectedClinic.procedures.find((procedure) => {
        const procedureClassPart = procedure.procedureName.toUpperCase();
        const selectedItemName =
          selectedProcedureClass[index].name.toUpperCase();

        // Modify the selected item name as needed for comparison
        let itemClassPart = selectedItemName
          .slice(selectedItemName.indexOf(":") + 2)
          .replace(" PROCEDURES", "");

        // Additional modifications for specific cases
        if (itemClassPart === "12 - L ECG") {
          itemClassPart = "ECG";
        } else if (itemClassPart === "2-D ECHO WITH DOPPLER STUDIES (PEDIA)") {
          itemClassPart = "2D ECHO (PEDIA)";
        } else if (itemClassPart === "2-D ECHO WITH DOPPLER STUDIES") {
          itemClassPart = "2D ECHO";
        }

        // Perform comparison
        return procedureClassPart.startsWith(itemClassPart);
      });
      const procedureIndex = selectedClinic.procedures.indexOf(foundProcedure);
      return selectedClinic.procedures[procedureIndex].availableTimes?.filter(
        (data) =>
          dayjs(data?.time).hour() < 18 && dayjs(data?.time).hour() >= 12
      );
    } else {
      const procedureToFind = selectedProcedures[index].coveredProcedures.map(
        (procedure) =>
          procedure.subclass ? procedure.subclass : procedure.class
      );

      const foundProcedure = selectedClinic.procedures.find((procedure) =>
        procedureToFind.some((item) => {
          const isSubclass = selectedProcedures[index].coveredProcedures.some(
            (p) => p.subclass === item
          );
          if (isSubclass) {
            return procedure.procedureName
              .toLowerCase()
              .includes(item.toLowerCase());
          } else {
            const procedureClassPart = procedure.procedureName
              .split(":")[0]
              .trim()
              .toLowerCase();
            const itemClassPart = item.split(" ")[0].trim().toLowerCase();
            return procedureClassPart.includes(itemClassPart);
          }
        })
      );

      if (foundProcedure) {
        const procedureIndex =
          selectedClinic.procedures.indexOf(foundProcedure);
        return selectedClinic.procedures[procedureIndex].availableTimes?.filter(
          (data) =>
            dayjs(data?.time).hour() < 18 && dayjs(data?.time).hour() >= 12
        );
      }
    }
  };

  const eveningTimes = (index) => {
    if (files.length > 0) {
      const foundProcedure = selectedClinic.procedures.find((procedure) => {
        const procedureClassPart = procedure.procedureName.toUpperCase();
        const selectedItemName =
          selectedProcedureClass[index].name.toUpperCase();

        // Modify the selected item name as needed for comparison
        let itemClassPart = selectedItemName
          .slice(selectedItemName.indexOf(":") + 2)
          .replace(" PROCEDURES", "");

        // Additional modifications for specific cases
        if (itemClassPart === "12 - L ECG") {
          itemClassPart = "ECG";
        } else if (itemClassPart === "2-D ECHO WITH DOPPLER STUDIES (PEDIA)") {
          itemClassPart = "2D ECHO (PEDIA)";
        } else if (itemClassPart === "2-D ECHO WITH DOPPLER STUDIES") {
          itemClassPart = "2D ECHO";
        }

        // Perform comparison
        return procedureClassPart.startsWith(itemClassPart);
      });
      const procedureIndex = selectedClinic.procedures.indexOf(foundProcedure);
      return selectedClinic.procedures[procedureIndex].availableTimes?.filter(
        (data) => dayjs(data?.time).hour() >= 18
      );
    } else {
      const procedureToFind = selectedProcedures[index].coveredProcedures.map(
        (procedure) =>
          procedure.subclass ? procedure.subclass : procedure.class
      );

      const foundProcedure = selectedClinic.procedures.find((procedure) =>
        procedureToFind.some((item) => {
          const isSubclass = selectedProcedures[index].coveredProcedures.some(
            (p) => p.subclass === item
          );
          if (isSubclass) {
            return procedure.procedureName
              .toLowerCase()
              .includes(item.toLowerCase());
          } else {
            const procedureClassPart = procedure.procedureName
              .split(":")[0]
              .trim()
              .toLowerCase();
            const itemClassPart = item.split(" ")[0].trim().toLowerCase();
            return procedureClassPart.includes(itemClassPart);
          }
        })
      );

      if (foundProcedure) {
        const procedureIndex =
          selectedClinic.procedures.indexOf(foundProcedure);
        return selectedClinic.procedures[procedureIndex].availableTimes?.filter(
          (data) => dayjs(data?.time).hour() >= 18
        );
      }
    }
  };
  const handleKeyDown = (event, callback) => {
    if (event.key === "Enter" || event.key === " ") {
      callback();
    }
  };

  const renderFirstColumn = (proceduresData, index) => {
    const renderProcedureName = () => {
      if (files.length > 0 || proceduresData?.coveredProcedures.length > 1) {
        return proceduresData.name.substring(
          proceduresData.name.indexOf(":") + 1
        );
      } else {
        return proceduresData?.coveredProcedures[0].name;
      }
    };

    const renderCoveredProcedures = () => {
      if (files?.length > 0) {
        return null;
      } else if (proceduresData.coveredProcedures.length > 1) {
        return proceduresData.coveredProcedures.map((data) => (
          <p key={data.name} className="text-sm">
            - {data.name}
          </p>
        ));
      } else {
        return null;
      }
    };

    let dateContent = <p className="text-sm">No date selected</p>
    if(proceduresData?.date){
      dateContent = (
        <div className="flex flex-row">
          <button
            className={`${
              (proceduresData?.date && !proceduresData.time) || edit[index]
                ? "border-2 p-2 rounded-md cursor-pointer"
                : "cursor-default"
            }`}
            tabIndex={index}
            onClick={() =>
              (proceduresData?.date && !proceduresData.time) || edit[index]
                ? handleOpenDatePicker(index)
                : null
            }
            onKeyDown={(event) =>
              handleKeyDown(event, () =>
                (proceduresData?.date && !proceduresData.time) ||
                edit[index]
                  ? handleOpenDatePicker(index)
                  : null
              )
            }
          >
            {edit[index] || !proceduresData?.time ? (
              <CalendarTodayRounded fontSize={"small"} className="mr-1" />
            ) : null}
            {dayjs(proceduresData.date).format("MMMM DD, YYYY")}
          </button>
          <div className={`${edit[index] ? "p-2" : "ml-2"}`}>
            {proceduresData?.time ? (
              <>
                {dayjs(proceduresData?.time).format("h:mm A")}
                <CheckRounded
                  fontSize={"small"}
                  className="-mt-1 ml-1"
                  sx={{
                    color: "green",
                  }}
                />
              </>
            ) : null}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col flex-grow">
        <div className="font-bold">{renderProcedureName()}</div>
        <div className="text-sm">
          {dateContent}
        </div>
        {/* procedures based on selected */}
        {renderCoveredProcedures()}
      </div>
    );
  };

  const renderSecondColumn = (proceduresData, index) => {
    return (
      <div className="flex flex-col justify-center pr-4">
        {(proceduresData?.date && !proceduresData?.time) || edit[index] ? (
          <div className="flex flex-row flex-grow">
            <button
              className="flex flex-col h-14 p-3 mx-2 items-center border rounded cursor-pointer flex-grow"
              tabIndex={index}
              onClick={() => previousDay(proceduresData, index)}
              onKeyDown={(event) =>
                handleKeyDown(event, () => previousDay(proceduresData, index))
              }
            >
              <KeyboardArrowLeftOutlined className="mt-1" />
            </button>
            <button
              className="flex flex-col h-14 p-3 items-center border rounded cursor-pointer flex-grow"
              tabIndex={index}
              onClick={() => nextDay(proceduresData, index)}
              onKeyDown={(event) =>
                handleKeyDown(event, () => nextDay(proceduresData, index))
              }
            >
              <KeyboardArrowRightOutlined className="mt-1" />
            </button>
          </div>
        ) : (
          <button
            className="cursor-pointer text-[#0947AB] font-semibold"
            onClick={() => {
              proceduresData?.date
                ? handleEditClick(index)
                : handleOpenDatePicker(index);
            }}
            onKeyDown={(event) =>
              handleKeyDown(event, () => {
                proceduresData?.date
                  ? handleEditClick(index)
                  : handleOpenDatePicker(index);
              })
            }
          >
            {proceduresData?.date ? "Edit" : "Select date"}
          </button>
        )}
        {renderDatePicker(proceduresData, index)}
      </div>
    );
  };

  let procedures = selectedProcedures;
  if(files.length>0){
    procedures = selectedProcedureClass;
  }

  let content = null;
  if(availableDateLoadState === "get"){
    content = (
      <div className="w-full flex justify-center items-center mt-4">
        <CircularProgress />
      </div>
    );
  } else if (["error", "retry"].includes(availableDateLoadState)) {
    content = (
      <ErrorComponent
        onRetry={() => fetchAvailableDates("retry")}
        isLoading={availableDateLoadState === "retry"}
      />
    );
  } else {
    content = procedures.map((item, index) => (
      <div className="px-3 mb-2" key={item.name}>
        <div className="flex flex-row p-3 border rounded-xl">
          <div className="flex flex-col w-full">
            <div className="flex flex-row">
              {/* first column (procedures, selected date) */}
              {renderFirstColumn(item, index)}
              {/* second column (select date button)*/}
              {renderSecondColumn(item, index)}
            </div>
            {(item?.date && !item?.time) || edit[index] ? (
              <>
                <div className="mx-2 mt-3">
                  {renderTimeSection("Morning", morningTimes(index), index)}
                  {renderTimeSection("Afternoon", afternoonTimes(index), index)}
                  {renderTimeSection("Evening", eveningTimes(index), index)}
                </div>
                {edit[index] ? (
                  <div className="flex justify-end my-2">
                    <button
                      onClick={() => handleEditClick(index)}
                      onKeyDown={(event) =>
                        handleKeyDown(event, () => handleEditClick(index))
                      }
                      className="text-[#0947AB] font-semibold border rounded-md py-1 px-2 cursor-pointer"
                    >
                      Done
                    </button>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>
    ));
  }

  return (
    <ScrollArea.Root className="w-full overflow-hidden">
      <ScrollArea.Viewport className="w-full min-h-[200px] h-[calc(100vh-266px)] sm:h-full sm:max-h-[400px] rounded">
        {content}
      </ScrollArea.Viewport>
      <ScrollBar />
    </ScrollArea.Root>
  );
};

export default ProceduresBooking;
